import { Component, AfterViewInit, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { DarkTheme } from '@amcharts/amcharts5/.internal/themes/DarkTheme';
import { ToggleDarkModeService } from '../../Shared/toggle-dark-mode.service';
import { THREAT_COLORS } from '../../Shared/threat-colors';

const NAICS_SECTOR_NAMES: Record<string, string> = {
    '11': 'Agriculture, Forestry, Fishing and Hunting',
    '21': 'Mining, Quarrying, and Oil and Gas Extraction',
    '22': 'Utilities',
    '23': 'Construction',
    '31': 'Manufacturing', '32': 'Manufacturing', '33': 'Manufacturing',
    '42': 'Wholesale Trade',
    '44': 'Retail Trade', '45': 'Retail Trade',
    '48': 'Transportation and Warehousing', '49': 'Transportation and Warehousing',
    '51': 'Information',
    '52': 'Finance and Insurance',
    '53': 'Real Estate and Rental and Leasing',
    '54': 'Professional, Scientific, and Technical Services',
    '55': 'Management of Companies and Enterprises',
    '56': 'Administrative and Support and Waste Management and Remediation Services',
    '61': 'Educational Services',
    '62': 'Health Care and Social Assistance',
    '71': 'Arts, Entertainment, and Recreation',
    '72': 'Accommodation and Food Services',
    '81': 'Other Services',
    '92': 'Public Administration'
};

const THREAT_TYPE_COLORS: Record<string, number> = {
    'Attack':    0xe74c3c,
    'Scan':      0x54e4a5,
    'Bot':       0xd62c15,
    'Malware':   0x0d9ed9,
    'Proxy':     0x8e44ad,
    'Spam':      0x4646da,
    'C2':        0xfb842a,
    'Reflector': 0xf39c12,
    'Suspicious':0xf4d03f,
    'Unknown':   0x888888,
};

@Component({
    selector: 'app-industry-threat-linechart',
    templateUrl: './industry-threat-linechart.component.html',
    styleUrls: ['./industry-threat-linechart.component.css'],
    standalone: false
})
export class IndustryThreatLinechartComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input() data: any[] = [];
    @Input() isDarkTheme = false;
    @Input() timeline = 'last 24 hours';
    @ViewChild('chartdiv', { static: false }) chartDiv!: ElementRef<HTMLDivElement>;
    attackTypeSelections: Record<string, boolean> = {};
    industrySelections: Record<string, boolean> = {};

    availableAttackTypes: string[] = [];
    availableIndustries: string[] = [];
    attackTypeDropdownOpen = false;
    industryDropdownOpen = false;
    isMinimized = false;

    // amcharts refs
    private root: any;
    private chart: any;
    private xAxis: any;
    private firstLaunch = true;

    constructor(private helper: ToggleDarkModeService) {}

    ngOnInit(): void {
        this.helper.selected_mode$.subscribe(mode => {
            this.isDarkTheme = mode;
            this.applyTheme();
        });
    }

    ngAfterViewInit(): void {
        this.buildChart();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.firstLaunch && (changes['data'] || changes['timeline'])) {
            this.extractFilters();
            this.renderSeries();
        }
    }

    ngOnDestroy(): void {
        if (this.root) {
            this.root.dispose();
        }
    }

    private buildChart(): void {
        this.root = am5.Root.new(this.chartDiv.nativeElement);
        this.applyTheme();

        this.chart = this.root.container.children.push(
            am5xy.XYChart.new(this.root, {
                panX: true,
                panY: false,
                wheelX: 'panX',
                wheelY: 'zoomX',
                pinchZoomX: true,
                layout: this.root.verticalLayout
            })
        );

        const cursor = am5xy.XYCursor.new(this.root, { behavior: 'zoomX' });
        cursor.lineY.set('visible', false);
        this.chart.set('cursor', cursor);
        this.chart.set('scrollbarX', am5.Scrollbar.new(this.root, { orientation: 'horizontal' }));

        // x axis
        const xRenderer = am5xy.AxisRendererX.new(this.root, { minGridDistance: 50 });
        xRenderer.labels.template.setAll({
            rotation: -45,
            centerY: am5.p50,
            centerX: am5.p100,
            paddingRight: 5,
            fontSize: 11
        });

        this.xAxis = this.chart.xAxes.push(
            am5xy.CategoryAxis.new(this.root, {
                categoryField: 'period',
                renderer: xRenderer
            })
        );

        // Y axis
        this.chart.yAxes.push(
            am5xy.ValueAxis.new(this.root, {
                logarithmic: true,
                baseLog: 10,
                maxDeviation: 0.3,
                renderer: am5xy.AxisRendererY.new(this.root, { strokeOpacity: 0.1 })
            })
        );

        this.extractFilters();
        this.renderSeries();
        this.firstLaunch = false;
    }

    // to filter
    private extractFilters(): void {
        const typeSet = new Set<string>();
        const industrySet = new Set<string>();

        if (Array.isArray(this.data) && this.data.length) {
            // [{hour, naics_sector, threat_type, avg_value, count}]
            for (const entry of this.data) {
                if (entry.threat_type) {
                    typeSet.add(entry.threat_type);
                }
                const sector = entry.naics_sector || '';
                if (sector && sector !== 'Unknown') {
                    industrySet.add(NAICS_SECTOR_NAMES[sector] || ('NAICS ' + sector));
                }
            }
        }

        // empty case
        if (typeSet.size === 0) {
            Object.keys(THREAT_TYPE_COLORS).forEach(t => typeSet.add(t));
        }

        this.availableAttackTypes = [...Array.from(typeSet).sort(), 'Unknown'];
        this.availableIndustries = [...Array.from(industrySet).sort(), 'Unknown'];

        const newTypeSelections: Record<string, boolean> = {};
        const hadTypes = Object.keys(this.attackTypeSelections).length > 0;
        this.availableAttackTypes.forEach(t => {
            newTypeSelections[t] = hadTypes ? (this.attackTypeSelections[t] ?? true) : true;
        });
        this.attackTypeSelections = newTypeSelections;

        const newIndustrySelections: Record<string, boolean> = {};
        const hadIndustries = Object.keys(this.industrySelections).length > 0;
        this.availableIndustries.forEach(i => {
            newIndustrySelections[i] = hadIndustries ? (this.industrySelections[i] ?? true) : true;
        });
        this.industrySelections = newIndustrySelections;
    }

    renderSeries(): void {
        if (!this.chart || !this.xAxis) return;

        const { periods, seriesData } = this.aggregateData();

        this.chart.series.values.slice().forEach((s: any) => s.dispose());
        this.chart.series.clear();

        const yAxis = this.chart.yAxes.getIndex(0);

        for (const [type, counts] of Object.entries(seriesData)) {
            const chartData = periods.map((p: string, i: number) => ({
                period: p,
                value: ((counts as number[])[i] > 0 ? (counts as number[])[i] : null)
            }));

            const seriesColor = THREAT_COLORS[type] ?? am5.color(0x0075c9);

            const series = this.chart.series.push(
                am5xy.SmoothedXLineSeries.new(this.root, {
                    name: type,
                    xAxis: this.xAxis,
                    yAxis: yAxis,
                    valueYField: 'value',
                    categoryXField: 'period',
                    stroke: seriesColor,
                    fill: seriesColor,
                    tooltip: am5.Tooltip.new(this.root, {
                        labelText: '{name}: [bold]{valueY}[/]'
                    })
                })
            );

            series.strokes.template.setAll({ strokeWidth: 2 });
            series.fills.template.setAll({ fillOpacity: 0, visible: false });

            series.data.setAll(chartData);
            series.appear(1000);
        }

        this.xAxis.data.setAll(periods.map((p: string) => ({ period: p })));
        this.chart.appear(1000, 100);
    }


    private aggregateData(): { periods: string[]; seriesData: Record<string, number[]> } 
    {
        if (!Array.isArray(this.data) || !this.data.length) 
        {
            return { periods: [], seriesData: {} };
        }

        // [{hour, naics_sector, threat_type, avg_value, count}]
        const DISPLAY_BUCKETS = 24;
        const periods: string[] = [];
        const attackTypes = this.availableAttackTypes.filter(t => this.attackTypeSelections[t]);
        const seriesData: Record<string, number[]> = {};
        for (const t of attackTypes) {
            seriesData[t] = new Array(DISPLAY_BUCKETS).fill(0);
        }
        const filteredEntries = this.getFilteredEntries(seriesData);

        if (this.timeline === 'last 24 hours') 
        {
            // 12 AM-11 PM
            for (let h = 0; h < 24; h++) {
                if (h === 0) periods.push('12 AM');
                else if (h < 12) periods.push(`${h} AM`);
                else if (h === 12) periods.push('12 PM');
                else periods.push(`${h - 12} PM`);
            }

            for (const entry of filteredEntries) {
                const threatType = entry.threat_type || 'Unknown';
                const bucketIdx = new Date(entry.hour).getUTCHours();
                seriesData[threatType][bucketIdx] += (entry.count ?? 0);
            }

        } 
        else 
        {
            // find time range for 7 and 30 days, spread 24 ticks evenly
            const allTimes = filteredEntries.map((e: any) => new Date(e.hour).getTime());

            if (!allTimes.length) return { periods: [], seriesData: {} };

            const minTime = Math.min(...allTimes);
            const maxTime = Math.max(...allTimes);
            const bucketMs = ((maxTime - minTime) || 1) / DISPLAY_BUCKETS + 1;

            for (let i = 0; i < DISPLAY_BUCKETS; i++) {
                const bucketTime = new Date(minTime + i * bucketMs);
                const month = bucketTime.getUTCMonth() + 1;
                const day = bucketTime.getUTCDate();
                const hour = bucketTime.getUTCHours().toString().padStart(2, '0');
                // Use unique timestamp labels to avoid duplicate category keys
                if (this.timeline === 'last 7 days') {
                    periods.push(`${month}/${day} ${hour}:00`);
                } else {
                    periods.push(`${month}/${day}`);
                }
            }

            // Sum rows per display bucket
            const bucketStats: Record<string, number[]> = {};
            for (const t of attackTypes) 
            {
                bucketStats[t] = new Array(DISPLAY_BUCKETS).fill(0);
            }

            for (const entry of filteredEntries) 
            {
                const threatType = entry.threat_type || 'Unknown';
                const entryTime = new Date(entry.hour).getTime();
                const bucketIdx = Math.min(
                    Math.floor((entryTime - minTime) / bucketMs),
                    DISPLAY_BUCKETS - 1
                );
                bucketStats[threatType][bucketIdx] += Number(entry.count ?? 0);
            }

            for (const t of attackTypes) 
            {
                for (let i = 0; i < DISPLAY_BUCKETS; i++) 
                {
                    seriesData[t][i] = Math.round(bucketStats[t][i]);
                }
            }
        }

        return { periods, seriesData };
    }

    private getFilteredEntries(seriesData: Record<string, number[]>): any[] {
        return this.data.filter((entry: any) => {
            if (!entry.hour) return false;
            const sector = entry.naics_sector || 'Unknown';
            const industryLabel = sector === 'Unknown' ? 'Unknown' : (NAICS_SECTOR_NAMES[sector] || ('NAICS ' + sector));
            if (!this.industrySelections[industryLabel]) return false;
            const threatType = entry.threat_type || 'Unknown';
            return seriesData[threatType] !== undefined;
        });
    }

    selectAllAttackTypes(): void {
        this.availableAttackTypes.forEach(t => this.attackTypeSelections[t] = true);
        this.renderSeries();
    }

    deselectAllAttackTypes(): void {
        this.availableAttackTypes.forEach(t => this.attackTypeSelections[t] = false);
        this.renderSeries();
    }

    selectAllIndustries(): void {
        this.availableIndustries.forEach(i => this.industrySelections[i] = true);
        this.renderSeries();
    }

    deselectAllIndustries(): void {
        this.availableIndustries.forEach(i => this.industrySelections[i] = false);
        this.renderSeries();
    }

    toggleAttackTypeDropdown(): void {
        this.attackTypeDropdownOpen = !this.attackTypeDropdownOpen;
        this.industryDropdownOpen = false;
    }

    toggleIndustryDropdown(): void {
        this.industryDropdownOpen = !this.industryDropdownOpen;
        this.attackTypeDropdownOpen = false;
    }

    closeDropdowns(): void {
        this.attackTypeDropdownOpen = false;
        this.industryDropdownOpen = false;
    }

    toggleMinimize(): void {
        this.isMinimized = !this.isMinimized;
    }

    private applyTheme(): void {
        if (!this.root) return;
        if (this.isDarkTheme) {
            this.root.setThemes([DarkTheme.new(this.root)]);
        } else {
            this.root.setThemes([am5themes_Animated.new(this.root)]);
        }
    }
}